import {encodePart} from '@github-ui/paths'
import {createContext, type ReactNode, use, useMemo} from 'react'

import type {SavedViewScope} from '../types'

// Where a saved view surface reads and writes its views.
//
// Repository views live under `/:owner/:repo/issues/views`, but other hosts may
// use paths unrelated to a repository. Rather than build URLs from `owner`/`repo`
// at each call site, hosts describe their endpoints once.
export interface SavedViewEndpoints {
  // Client route and write endpoint root. `${basePath}/${id}.json` is a single
  // view, `${basePath}.json` creates one.
  basePath: string
  // Where the list is fetched from. Separate from `basePath` because a surface
  // may serve its route payload and its plain list from different URLs.
  listUrl: string
}

const SavedViewEndpointsContext = createContext<SavedViewEndpoints | undefined>(undefined)
// Repository callers need no provider, while other hosts describe themselves once
// so shared components can use copy appropriate to that surface.
const SavedViewScopeContext = createContext<SavedViewScope>('repository')

export function SavedViewEndpointsProvider({
  endpoints,
  viewScope = 'repository',
  children,
}: {
  endpoints: SavedViewEndpoints
  viewScope?: SavedViewScope
  children: ReactNode
}) {
  return (
    <SavedViewEndpointsContext value={endpoints}>
      <SavedViewScopeContext value={viewScope}>{children}</SavedViewScopeContext>
    </SavedViewEndpointsContext>
  )
}

export function repositorySavedViewEndpoints(owner: string, repo: string): SavedViewEndpoints {
  const basePath = `/${encodePart(owner)}/${encodePart(repo)}/issues/views`
  return {basePath, listUrl: `${basePath}.json`}
}

// Falls back to the repository endpoints so every existing caller keeps working
// without a provider.
export function useSavedViewEndpoints(owner: string, repo: string): SavedViewEndpoints {
  const provided = use(SavedViewEndpointsContext)
  return useMemo(() => provided ?? repositorySavedViewEndpoints(owner, repo), [provided, owner, repo])
}

export function useSavedViewScope(): SavedViewScope {
  return use(SavedViewScopeContext)
}
